import React from 'react';
import Footer from '../components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowRight, Check, Users, Globe, Sparkles, Download, BarChart, Quote, Briefcase, Info, TrendingUp, FileCheck, Rocket, Code, Wallet, GitBranch, Brain, Star, Shield, Clock } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import PlaygroundValuesSection from '@/components/sections/PlaygroundValuesSection';
import OurPromiseSection from '@/components/sections/OurPromiseSection';

const PlaygroundPage: React.FC = () => {
  const {
    t,
    language
  } = useLanguage();
  
  return (
    <div className="min-h-screen bg-noa-antiflash-white">
      {/* No Navbar here anymore */}
      
      <main>
        {/* IMPROVED HERO SECTION - Modern Office Space with Updated Wording */}
        <section className="min-h-screen relative overflow-hidden flex items-center bg-gradient-to-r from-[#0D1F2D] to-[#2F4550]">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/lovable-uploads/f3aa7332-d1c4-4d3a-9c35-5bb87506cef7.png" 
              alt="Oficinas modernas de Noa Partners" 
              className="w-full h-full object-cover opacity-50"
            />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0D1F2D]/90 via-[#0D1F2D]/70 to-transparent z-10"></div>
            
            {/* Decorative elements */}
            <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-[#0D1F2D] to-transparent z-20"></div>
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-blue-400/10 blur-3xl rounded-full z-5"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-30">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Left Column - Text Content - Now stacked on the left */}
              <div className="lg:col-span-6 space-y-8">
                {/* Updated logo - making it smaller */}
                <img 
                  src="/lovable-uploads/513df9a4-391b-4147-b895-daeec7852f09.png" 
                  alt="Noa Partners Logo" 
                  className="h-8 w-auto"
                />
                
                <div className="space-y-4">
                  <h1 className="font-space-grotesk text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight text-left whitespace-nowrap">
                    Deja tu legado en las
                  </h1>
                  
                  <h1 className="font-space-grotesk text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight text-left whitespace-nowrap">
                    mejores manos<span className="text-blue-400">.</span>
                  </h1>
                </div>
                
                <p className="text-white/80 text-lg sm:text-xl max-w-2xl leading-relaxed text-left">
                  Adquirimos empresas de software B2B exitosas en Latinoamérica para conservarlas y hacerlas crecer.
                </p>
                
                {/* Stats - Changed +25 to 6 */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-8 border-t border-white/10">
                  <div>
                    <p className="text-3xl font-bold text-blue-400">6</p>
                    <p className="text-white/70 text-sm">Adquisiciones exitosas</p>
                  </div>
                  
                  <div>
                    <p className="text-3xl font-bold text-blue-400">100%</p>
                    <p className="text-white/70 text-sm">Retención de talento</p>
                  </div>
                  
                  <div>
                    <p className="text-3xl font-bold text-blue-400">∞</p>
                    <p className="text-white/70 text-sm">Compromiso a largo plazo</p>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6"></div>
            </div>
          </div>
        </section>
        
        {/* Business Partnership Section - Inspired by the image */}
        <section className="min-h-screen py-0 flex items-center bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left Column - Text Content - EXPANDED */}
              <div className="lg:col-span-7 space-y-8">
                <div className="inline-flex">
                  <Button variant="pill" size="pill" className="group transition-all duration-300">
                    <div className="h-4 w-4 rounded-full bg-gray-400 mr-2"></div>
                    <span className="uppercase text-sm tracking-wider font-medium">POR QUÉ EXISTIMOS</span>
                  </Button>
                </div>
                
                <h2 className="font-space-grotesk text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold text-noa-rich-black leading-tight max-w-2xl tracking-tight">
                  Has construido un gran negocio<span className="text-black">.</span> Mantengámoslo así<span className="text-blue-500">.</span>
                </h2>
                
                <p className="text-noa-charcoal/90 text-xl font-light leading-relaxed max-w-xl">
                  Cuando has dedicado años a crear un negocio exitoso de software empresarial, venderlo no es una decisión fácil.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-8">
                  <div>
                    <p className="text-noa-charcoal/80 text-base leading-relaxed">
                      Te importa profundamente tu negocio, tu equipo y tus clientes. Pero un fondo de inversión tradicional o un search fund probablemente revenderá tu empresa en el futuro. Y un comprador estratégico podría enfocarse en recortes, cambiar la cultura y transformar lo que con tanto esfuerzo construiste.
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    <p className="text-noa-charcoal/80 text-base leading-relaxed">
                      Nosotros creemos que mereces algo mejor al momento de vender. Un socio que proteja tu legado, respete tu historia y te acompañe en el crecimiento a largo plazo.
                    </p>
                    <p className="text-[#2F4550] border-l-2 border-[#2F4550] pl-4 py-1">
                      Bienvenido a Noa: el hogar permanente más confiable para tu negocio de software.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Image - REDUCED WIDTH */}
              <div className="lg:col-span-5 relative">
                <div className="relative h-[500px] rounded-lg overflow-hidden">
                  <img src="/lovable-uploads/796ec4ae-ba3d-4b0b-957e-54272be92291.png" alt="Ejecutivo senior reunido con profesionales" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* NEW SECTION - Benefits of Partnering with Noa */}
        <PlaygroundValuesSection />
        
        {/* MERGED SECTIONS - Nueva Perspectiva and Platform Offerings - REORGANIZED LAYOUT */}
        <section className="py-24 bg-gradient-to-br from-[#0f172a] to-[#334155] text-white overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Left Column - Title and Description */}
              <div className="lg:col-span-3 lg:sticky lg:top-24">
                <p className="inline-flex items-center justify-center gap-2 bg-blue-900/50 px-4 py-2 rounded-full text-blue-300 text-sm font-medium mb-4">
                  <Sparkles className="h-4 w-4" />
                  <span>Nueva perspectiva</span>
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-5 font-space-grotesk">
                  Transformando el futuro de tu empresa<span className="text-blue-400">.</span>
                </h2>
                <p className="text-base text-white/70 mb-6">
                  En Noa Partners no solo adquirimos empresas; creamos alianzas duraderas que protegen el legado que has construido mientras impulsamos su crecimiento hacia el futuro.
                </p>
                <div className="hidden lg:block mt-8">
                  <p className="text-lg text-white/80 font-light italic border-l-2 border-blue-400 pl-4 py-2">
                    "En Noa, construimos un ecosistema donde las empresas B2B de software pueden florecer con apoyo continuo."
                  </p>
                </div>
              </div>
              
              {/* Right Column - Cards Grid */}
              <div className="lg:col-span-9">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {/* Card 1 - Larger */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden md:col-span-2 lg:col-span-1 row-span-2">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-blue-500 to-purple-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-blue-600/20 rounded-lg w-14 h-14 flex items-center justify-center mb-6">
                      <BarChart className="w-7 h-7 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                      Experiencia en ventas y marketing
                    </h3>
                    <p className="text-white/70">
                      Estrategias probadas para acelerar el crecimiento, optimizar conversiones y expandir tu presencia en el mercado tanto local como internacional.
                    </p>
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <p className="text-blue-300 text-sm">Acceso a expertos en SEO, SEM y estrategias de conversión.</p>
                    </div>
                  </div>
                  
                  {/* Card 2 - Standard */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-green-500 to-emerald-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-green-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <Briefcase className="w-6 h-6 text-green-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-green-400 transition-colors">
                      Recursos operativos
                    </h3>
                    <p className="text-white/70 text-sm">
                      Herramientas y procesos para optimizar tus operaciones comerciales diarias.
                    </p>
                  </div>
                  
                  {/* Card 3 - Standard */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-amber-500 to-orange-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-amber-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-amber-400 transition-colors">
                      Comunidad Noa
                    </h3>
                    <p className="text-white/70 text-sm">
                      Conexión con una comunidad global de líderes, CEO's y expertos en tecnología y B2B Saas.
                    </p>
                  </div>
                  
                  {/* Card 4 - Wider */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden md:col-span-2">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-rose-500 to-pink-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="flex items-start">
                      <div className="p-3 bg-rose-600/20 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                        <Download className="w-6 h-6 text-rose-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-rose-400 transition-colors">
                          Recursos tecnológicos
                        </h3>
                        <p className="text-white/70 text-sm">
                          Infraestructura, herramientas de desarrollo y soporte técnico para mejorar tus productos y servicios. Acceso a las últimas tecnologías y mejores prácticas en desarrollo de software.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card 5 - Standard */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-indigo-500 to-blue-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-indigo-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <Globe className="w-6 h-6 text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-indigo-400 transition-colors">
                      Expansión global
                    </h3>
                    <p className="text-white/70 text-sm">
                      Estrategias para entrar en nuevos mercados internacionales.
                    </p>
                  </div>
                  
                  {/* Card 6 - Standard */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-purple-500 to-violet-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-purple-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <Sparkles className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-purple-400 transition-colors">
                      Consultoría estratégica
                    </h3>
                    <p className="text-white/70 text-sm">
                      Asesoramiento para navegar desafíos empresariales complejos.
                    </p>
                  </div>
                  
                  {/* Card 7 - NEW Strategic M&A */}
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:bg-white/10 transition-all group relative overflow-hidden">
                    <div className="absolute h-1 w-0 bg-gradient-to-r from-teal-500 to-cyan-500 bottom-0 left-0 group-hover:w-full transition-all duration-300"></div>
                    <div className="p-3 bg-teal-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <GitBranch className="w-6 h-6 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-teal-400 transition-colors">
                      M&A estratégico
                    </h3>
                    <p className="text-white/70 text-sm">
                      Crecer a través de adquisiciones complementarias, ampliando ofertas y entrando en nuevos mercados.
                    </p>
                  </div>
                  
                  {/* Card 8 - Tall with CEO quote - Mobile Only */}
                  <div className="lg:hidden bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm md:col-span-2">
                    <div className="flex items-center mb-6">
                      <Avatar className="h-10 w-10 border-2 border-white/10 mr-3">
                        <AvatarFallback>MB</AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <p className="font-medium">Miguel Bustos</p>
                        <p className="text-sm text-blue-300">CEO, Noa Partners</p>
                      </div>
                    </div>
                    <p className="text-white/80 font-light italic">
                      "En Noa, no solo adquirimos empresas; construimos un ecosistema donde las empresas B2B de software pueden florecer con apoyo continuo y recursos dedicados."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* NEW SECTION - Success Case Study - Ambit Technologies - ADJUSTED TO FULL PAGE */}
        <section className="h-screen flex items-center justify-center bg-gradient-to-br from-[#0D1F2D] via-[#1A3A5A] to-[#0A1825] relative overflow-hidden">
          {/* Enhanced background elements with deep blue gradients */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-[#0D1F2D]/30 via-[#1A3A5A]/20 to-transparent blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-1/2 h-full bg-gradient-to-tl from-[#1A3A5A]/30 via-[#0D1F2D]/20 to-transparent blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center gap-2 bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-blue-400 text-sm font-medium mb-3">
                <FileCheck className="h-4 w-4" />
                <span>Success Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 font-space-grotesk text-white">
                Ambit Technologies<span className="text-blue-400">.</span>
              </h2>
              <p className="text-base text-blue-100/80 max-w-2xl mx-auto">
                Acquired in 2023, a story of continuous growth and global expansion
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Left side - Image with CEO quote */}
              <div className="lg:col-span-5 relative">
                <div className="relative rounded-2xl overflow-hidden bg-white dark:bg-[#2F4550]/30 shadow-xl border border-gray-100 dark:border-[#2F4550]/50">
                  <img src="/lovable-uploads/5ac2a757-9557-4b0b-acfb-3b0b16476490.png" alt="Ambit Technologies CEO" className="w-full h-64 object-cover object-center" />
                  
                  {/* CEO Quote overlay */}
                  <div className="p-5 bg-white dark:bg-[#2F4550]/90">
                    <div className="flex items-start mb-3">
                      <Quote className="text-blue-500 w-8 h-8 mt-1 mr-3 flex-shrink-0" />
                      <p className="text-[#2F4550] dark:text-white/90 italic text-sm">
                        "Joining Noa Partners has been transformative for Ambit. We have maintained our identity while accelerating our growth and global expansion."
                      </p>
                    </div>
                    
                    <div className="flex items-center">
                      <Avatar className="h-10 w-10 mr-3 border-2 border-blue-100 dark:border-blue-900">
                        <AvatarImage src="/lovable-uploads/53c30fa2-ef72-4e12-af40-735ee8b46ca6.png" alt="Allan Cohen Arditti" />
                      </Avatar>
                      <div>
                        <h4 className="font-bold text-[#0D1F2D] dark:text-white text-sm">Allan Cohen Arditti</h4>
                        <p className="text-xs text-blue-600 dark:text-blue-300">CEO, Ambit Technologies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - Achievement metrics */}
              <div className="lg:col-span-7">
                <div className="bg-white backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-lg">
                  <h3 className="text-xl font-bold text-[#0D1F2D] dark:text-white mb-4">
                    Achievements Since Acquisition in 2023
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Achievement 1 - Growth (Moved to first position) */}
                    <div className="flex items-start">
                      <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-lg mr-3">
                        <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2F4550] dark:text-white/90 mb-1 text-sm">2x Growth</h4>
                        <p className="text-[#586F7C] dark:text-white/70 text-xs">
                          Doubled revenue and customer base in two years
                        </p>
                      </div>
                    </div>

                    {/* Achievement 2 - Strong Returns */}
                    <div className="flex items-start">
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3">
                        <BarChart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2F4550] dark:text-white/90 mb-1 text-sm">Strong Returns</h4>
                        <p className="text-[#586F7C] dark:text-white/70 text-xs">
                          30% IRR achieved during past 24 months
                        </p>
                      </div>
                    </div>

                    {/* Achievement 3 - Market Expansion */}
                    <div className="flex items-start">
                      <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg mr-3">
                        <Globe className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2F4550] dark:text-white/90 mb-1 text-sm">Market Expansion</h4>
                        <p className="text-[#586F7C] dark:text-white/70 text-xs">
                          Successful entry into 4 new international markets
                        </p>
                      </div>
                    </div>
                    
                    {/* Achievement 4 - Strategic Acquisitions */}
                    <div className="flex items-start">
                      <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg mr-3">
                        <Briefcase className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2F4550] dark:text-white/90 mb-1 text-sm">Strategic Acquisitions</h4>
                        <p className="text-[#586F7C] dark:text-white/70 text-xs">
                          4 complementary acquisitions to expand offering
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Leadership continuity highlight */}
                  <div className="mt-6 bg-gray-100 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center">
                      <div className="bg-blue-500 p-1.5 rounded-full mr-3">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-sm">Leadership Continuity</h4>
                        <p className="text-gray-600 text-xs">
                          The CEO remained in his position and has led the business forward, working closely with the Noa Partners team
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* NEW SECTION - What Companies We Seek and Simple Process - SIDE BY SIDE */}
        <section className="py-20 bg-white dark:bg-[#0D1F2D] relative overflow-hidden min-h-screen">
          {/* Background subtle elements */}
          <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-50/30 to-transparent dark:from-blue-900/10 dark:to-transparent"></div>
          <div className="absolute top-20 right-40 w-72 h-72 bg-green-100/40 dark:bg-green-900/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              {/* Left Column - What Companies We Seek */}
              <div>
                <div className="inline-flex items-center justify-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Star className="w-3.5 h-3.5" />
                  <span>Empresas que buscamos</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-space-grotesk text-[#0D1F2D] dark:text-white">
                  Empresas con las que buscamos asociarnos<span className="text-blue-500">.</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Criteria 1 */}
                  <div className="flex items-start space-x-3 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white mb-1">Empresas de Software</h3>
                      <p className="text-sm text-[#586F7C] dark:text-white/70">SaaS o licencias con ingresos recurrentes</p>
                    </div>
                  </div>
                  
                  {/* Criteria 2 */}
                  <div className="flex items-start space-x-3 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white mb-1">$2mm - $15mm</h3>
                      <p className="text-sm text-[#586F7C] dark:text-white/70">Ingresos anuales recurrentes</p>
                    </div>
                  </div>
                  
                  {/* Criteria 3 */}
                  <div className="flex items-start space-x-3 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white mb-1">B2B y Misión Crítica</h3>
                      <p className="text-sm text-[#586F7C] dark:text-white/70">Productos integrados en los flujos de trabajo</p>
                    </div>
                  </div>
                  
                  {/* Criteria 4 */}
                  <div className="flex items-start space-x-3 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 text-white">
                      <Check className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white mb-1">Crecimiento Sostenido</h3>
                      <p className="text-sm text-[#586F7C] dark:text-white/70">Al menos 10% de crecimiento anual durante los últimos 3 años</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-5 bg-[#0D1F2D]/5 dark:bg-white/5 rounded-lg border border-[#0D1F2D]/10 dark:border-white/10">
                  <p className="text-[#2F4550] dark:text-white/80 italic">
                    "Buscamos empresas con productos establecidos, clientes leales y un sólido historial de generación de valor."
                  </p>
                </div>
              </div>
              
              {/* Right Column - Simple Process */}
              <div>
                <div className="inline-flex items-center justify-center gap-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <ArrowRight className="w-3.5 h-3.5" />
                  <span>Proceso sencillo</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-space-grotesk text-[#0D1F2D] dark:text-white">
                  Simple y muy personal<span className="text-green-500">.</span>
                </h2>
                
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 font-bold text-lg">
                      1
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white text-xl mb-2">Llamada de descubrimiento</h3>
                      <p className="text-[#586F7C] dark:text-white/70 text-sm">
                        Conversación inicial para conocernos y entender si hay un encaje entre tu negocio y nuestro enfoque de adquisición a largo plazo.
                      </p>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300 font-bold text-lg">
                      2
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white text-xl mb-2">Análisis colaborativo</h3>
                      <p className="text-[#586F7C] dark:text-white/70 text-sm">
                        Proceso transparente donde compartimos nuestros hallazgos y construimos juntos un plan preliminar de crecimiento post-adquisición.
                      </p>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300 font-bold text-lg">
                      3
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white text-xl mb-2">Oferta y cierre</h3>
                      <p className="text-[#586F7C] dark:text-white/70 text-sm">
                        Oferta clara con términos justos y flexibles, seguida de un proceso de cierre eficiente con apoyo de nuestro equipo legal y financiero.
                      </p>
                    </div>
                  </div>
                  
                  {/* Step 4 */}
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 font-bold text-lg">
                      4
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2F4550] dark:text-white text-xl mb-2">Transición y crecimiento</h3>
                      <p className="text-[#586F7C] dark:text-white/70 text-sm">
                        Plan de transición personalizado y ejecución de la estrategia de crecimiento a largo plazo con equipo de apoyo dedicado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* UPDATED SECTION - Final words with founding team image */}
        <section className="py-20 bg-gradient-to-r from-[#0D1F2D] to-[#2F4550] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left Column - Text Content */}
              <div className="lg:col-span-7">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 font-space-grotesk">
                  Noa, Un Socio en Quien Confiar<span className="text-blue-400">.</span>
                </h2>
                <p className="text-lg text-white/80 mb-8">
                  En Noa Partners, vemos la venta de tu empresa no solo como una transacción financiera, sino como un hito significativo que merece celebrarse - un nuevo y prometedor capítulo para tu negocio y tu vida. Como inversores comprometidos a largo plazo, nos dedicamos a asegurar el éxito duradero de tu legado.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* Promise Item 1 */}
                  <div className="bg-white/5 border border-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="bg-blue-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <Shield className="text-blue-400 w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Continuidad Empresarial</h3>
                    <p className="text-white/70">
                      Garantizamos que tu negocio siga prosperando con una administración cuidadosa y planificación estratégica.
                    </p>
                  </div>
                  
                  {/* Promise Item 2 */}
                  <div className="bg-white/5 border border-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="bg-green-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <Users className="text-green-400 w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Preservación cultural</h3>
                    <p className="text-white/70">
                      Preservamos la cultura única y los valores que han hecho que tu negocio tenga éxito.
                    </p>
                  </div>
                  
                  {/* Promise Item 3 */}
                  <div className="bg-white/5 border border-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="bg-amber-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <TrendingUp className="text-amber-400 w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Hacer Crecer Tu Empresa</h3>
                    <p className="text-white/70">
                      Invertimos en el desarrollo de tu equipo y creamos nuevas oportunidades de crecimiento y avance.
                    </p>
                  </div>
                </div>
                
                <div className="bg-white/5 border border-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <p className="text-lg font-light italic text-white/80">
                    "Nuestra promesa es sencilla: preservar lo que hace especial a tu empresa mientras proporcionamos los recursos y el apoyo para amplificar su impacto en el mercado."
                  </p>
                  <div className="mt-6 flex justify-end">
                    <Button variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10">
                      <span>Comenzar la conversación</span>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Founding Team Image */}
              <div className="lg:col-span-5">
                <div className="relative rounded-xl overflow-hidden border-4 border-white/10">
                  <img 
                    src="/lovable-uploads/d0daf4ac-3882-4e25-ae63-7f0fb2b81b9d.png" 
                    alt="Equipo fundador de Noa Partners" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D1F2D]/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white mb-2">Equipo Fundador</h3>
                    <p className="text-white/80 text-sm">
                      Construyendo el futuro de las adquisiciones de software B2B en América Latina.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </main>
    </div>
  );
};

export default PlaygroundPage;
